import React, { useMemo } from "react";
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import {
  formatCompactAge,
  formatLongAge,
  getAgeMetrics,
} from "../services/claimAgeUtil";

/**
 * ClaimAge — colour-coded age since extraction, scaled against the average
 * age of the invoices currently in view (newest → green, oldest → red).
 *
 * variant="table"  — relative age + muted absolute date + heat bar
 * variant="card"   — compact chip for the board/grid cards
 */
const ClaimAge = ({
  extractedAt,
  avgAgeMs,
  absoluteLabel,
  variant = "table",
  className = "",
}) => {
  const { t } = useTranslation();
  const locale = t("currentLanguage") || "en";

  const metrics = useMemo(
    () => getAgeMetrics(extractedAt, avgAgeMs),
    [extractedAt, avgAgeMs]
  );

  if (!metrics) {
    return (
      <span className={`claim-age claim-age--empty ${className}`.trim()}>
        {t("notAvailable")}
      </span>
    );
  }

  const compact = formatCompactAge(metrics.ageMs, locale);
  const longAge = formatLongAge(metrics.ageMs, locale);
  const avgLabel = formatLongAge(metrics.avgAgeMs, locale);
  const vsAvg =
    metrics.ratio < 0.95
      ? t("claimAgeNewerThanAvg", { ratio: metrics.ratio.toFixed(1) })
      : metrics.ratio > 1.05
      ? t("claimAgeOlderThanAvg", { ratio: metrics.ratio.toFixed(1) })
      : t("claimAgeAboutAvg");

  const tooltip = (
    <div className="claim-age-tooltip">
      <div className="claim-age-tooltip-title">
        {t(`claimAgeTier_${metrics.tier}`)}
      </div>
      <div>{longAge}</div>
      {absoluteLabel ? <div className="claim-age-tooltip-abs">{absoluteLabel}</div> : null}
      <div className="claim-age-tooltip-meta">
        {t("claimAgeAvgBaseline", { age: avgLabel })}
      </div>
      <div className="claim-age-tooltip-meta">{vsAvg}</div>
    </div>
  );

  const style = {
    "--age-solid": metrics.solid,
    "--age-soft": metrics.soft,
    "--age-border": metrics.border,
    "--age-heat": `${metrics.heat}%`,
  };

  if (variant === "card") {
    return (
      <Tooltip title={tooltip} placement="top">
        <span
          className={`claim-age claim-age--card claim-age--${metrics.tier} ${className}`.trim()}
          style={style}
          aria-label={`${t("claimAge")}: ${longAge}`}
        >
          <span className="claim-age-dot" aria-hidden="true" />
          <span className="claim-age-label">{compact}</span>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltip} placement="topLeft">
      <div
        className={`claim-age claim-age--table claim-age--${metrics.tier} ${className}`.trim()}
        style={style}
        aria-label={`${t("claimAge")}: ${longAge}`}
      >
        <div className="claim-age-row">
          <span className="claim-age-dot" aria-hidden="true" />
          <span className="claim-age-label">{compact}</span>
          <span className="claim-age-tier">{t(`claimAgeTier_${metrics.tier}`)}</span>
        </div>
        <div className="claim-age-heat" aria-hidden="true">
          <span className="claim-age-heat-avg" title={t("claimAgeAverageMark")} />
          <span className="claim-age-heat-needle" />
        </div>
      </div>
    </Tooltip>
  );
};

/** Small legend for the column / board header. */
export const ClaimAgeLegend = () => {
  const { t } = useTranslation();
  return (
    <Tooltip
      title={
        <div className="claim-age-tooltip">
          <div className="claim-age-tooltip-title">{t("claimAgeLegendTitle")}</div>
          <div>{t("claimAgeLegendBody")}</div>
        </div>
      }
      placement="top"
    >
      <span className="claim-age-legend" tabIndex={0}>
        <span className="claim-age-legend-swatch claim-age-legend-swatch--fresh" />
        <span className="claim-age-legend-swatch claim-age-legend-swatch--mid" />
        <span className="claim-age-legend-swatch claim-age-legend-swatch--stale" />
        <span className="claim-age-legend-text">{t("claimAge")}</span>
      </span>
    </Tooltip>
  );
};

export default ClaimAge;
